import { demoFullSections, demoWritingSections } from '@/lib/demo-test-definitions'
import type { LeaderboardEntry, LeaderboardResponse, TestDefinition, TestResult, UserProfile } from '@/lib/types'

export const demoProfile: UserProfile = {
  id: 'demo-student',
  first_name: 'Noura',
  last_name: 'Al-Khatib',
  full_name: 'Noura Al-Khatib',
  email: 'noura@example.com',
  user: {
    id: 'demo-user',
    email: 'noura@example.com',
    role: 'student',
  },
}

export const demoTests: TestDefinition[] = [
  {
    id: 'demo-reading',
    title: 'Academic Reading Practice 01',
    display_title: 'Academic Reading Practice 01',
    description: 'An IELTS-style reading paper with the passage and questions side by side.',
    types: ['Reading'],
    level: 'Advanced',
    time_limit: 60,
    is_active: true,
    tests_taken: 128,
    sections: [
      {
        id: 'reading-section',
        title: 'Reading',
        type: 'Reading',
        time_limit_minutes: 60,
        parts: [
          {
            id: 'reading-part-2',
            name: 'Passage 2',
            title: 'Cooling the modern city',
            order: 2,
            context_text: `<p><strong>Cooling the modern city</strong></p>
              <p><b>A</b> Cities are often warmer than their rural surroundings. The urban heat island effect is not new, but a growing body of research suggests that the shape and material of neighbourhoods can determine how severe it becomes.</p>
              <p><b>B</b> One response is to increase tree cover. Mature trees shade buildings, intercept sunlight and release water vapour. Yet planting programmes succeed only when species, soil depth and long-term maintenance are considered together.</p>
              <p><b>C</b> Researchers in Singapore found that connected corridors of vegetation produced more reliable evening cooling than separate small parks. The benefit continued after sunset because shaded surfaces had absorbed less heat during the day.</p>
              <p><b>D</b> Reflective roofs offer another route. Light-coloured surfaces send a larger proportion of solar energy back into the atmosphere, although their performance varies with climate, roof angle and surrounding buildings.</p>
              <p><b>E</b> The most successful strategies are rarely singular. City planners increasingly combine shade, reflective surfaces, ventilation corridors and lower-emission transport.</p>`,
            instructions: 'Read the passage and answer Questions 18–20.',
            question_groups: [
              {
                id: 'tfng-group',
                title: 'Questions 18–20',
                instructions: 'Do the following statements agree with the information given in the passage?',
                group_order: 1,
                questions: [
                  {
                    id: 'q18',
                    number: 18,
                    text: 'All tree species provide the same level of cooling.',
                    type: 'true_false_not_given',
                    points_value: 1,
                    type_data: {},
                  },
                  {
                    id: 'q19',
                    number: 19,
                    text: 'Connected green areas can outperform separate parks.',
                    type: 'true_false_not_given',
                    points_value: 1,
                    type_data: {},
                  },
                  {
                    id: 'q20',
                    number: 20,
                    text: 'Reflective roofs are equally effective in every climate.',
                    type: 'true_false_not_given',
                    points_value: 1,
                    type_data: {},
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'demo-full',
    title: 'Academic Mock 04',
    display_title: 'Academic Mock 04',
    description: 'Full Listening, Reading, Writing and Speaking simulation.',
    types: ['Listening', 'Reading', 'Writing', 'Speaking'],
    level: 'Advanced',
    time_limit: 164,
    is_active: true,
    tests_taken: 342,
    sections: demoFullSections,
  },
  {
    id: 'demo-writing',
    title: 'Writing Coherence Diagnostic',
    display_title: 'Writing Coherence Diagnostic',
    description: 'Two focused writing tasks calibrated to your recent results.',
    types: ['Writing'],
    level: 'Intermediate',
    time_limit: 60,
    is_active: true,
    tests_taken: 96,
    sections: demoWritingSections,
  },
]

export const demoResults: TestResult[] = [
  {
    id: 'demo-result-abandoned',
    test_id: 'demo-full',
    test_title: 'Academic Mock 01',
    score: 48,
    max_score: 80,
    completed_at: '2026-08-04T11:20:00Z',
    percentage_score: 60,
    time_spent: 4_860,
    attempt_number: 1,
    tab_switch_count: 0,
    test_available: true,
    can_retake: true,
    status: 'abandoned',
    grading_status: 'succeeded',
    score_publication_status: 'not_applicable',
    section_scores: {
      listening: { band: 6.5, score: 28, max_score: 40 },
      reading: { band: 6, score: 26, max_score: 40 },
      writing: { band: 5.5, score: 5.5, max_score: 9 },
      overall_band: 6,
    },
  },
  {
    id: 'demo-result',
    test_id: 'demo-full',
    test_title: 'Academic Mock 03',
    score: 59,
    max_score: 80,
    completed_at: '2026-08-02T14:30:00Z',
    percentage_score: 73.75,
    time_spent: 8_940,
    attempt_number: 3,
    tab_switch_count: 0,
    test_available: true,
    grading_status: 'succeeded',
    score_publication_status: 'published',
    section_scores: {
      listening: { band: 7, score: 31, max_score: 40 },
      reading: { band: 6.5, score: 28, max_score: 40 },
      writing: { band: 6, score: 6, max_score: 9 },
      speaking: { band: 6.5, score: 6.5, max_score: 9 },
      overall_band: 6.5,
    },
  },
  {
    id: 'demo-result-pending',
    test_id: 'demo-writing',
    test_title: 'Writing Coherence Diagnostic',
    score: 0,
    max_score: 18,
    completed_at: '2026-07-29T18:15:00Z',
    percentage_score: 0,
    time_spent: 3_410,
    attempt_number: 1,
    grading_status: 'running',
    score_publication_status: 'not_applicable',
    test_available: true,
    section_scores: {
      writing: { score: 0, max_score: 18 },
    },
  },
  {
    id: 'demo-result-not-attempted',
    test_id: 'demo-full',
    test_title: 'Academic Mock 04',
    score: 56,
    max_score: 89,
    completed_at: '2026-08-10T09:15:00Z',
    percentage_score: 63,
    time_spent: 7_260,
    attempt_number: 1,
    tab_switch_count: 0,
    test_available: true,
    can_retake: true,
    grading_status: 'succeeded',
    score_publication_status: 'published',
    section_scores: {
      listening: { band: 6.5, score: 26, max_score: 40 },
      reading: { band: 6, score: 24, max_score: 40 },
      writing: { band: 6.5, score: 6.5, max_score: 9 },
      speaking: {
        score: 0,
        max_score: 9,
        band: null,
        not_attempted: true,
        not_attempted_reason: 'No answers were given for this section, so it was not assessed.',
      },
      overall_band: 6.5,
    },
  },
  {
    id: 'demo-result-review',
    test_id: 'demo-full',
    test_title: 'Academic Mock 02',
    score: 52,
    max_score: 80,
    completed_at: '2026-07-21T09:40:00Z',
    percentage_score: 65,
    time_spent: 9_320,
    attempt_number: 2,
    tab_switch_count: 3,
    grading_status: 'needs_review',
    score_publication_status: 'withheld',
    test_available: true,
    section_scores: {
      listening: { band: 6.5, score: 27, max_score: 40 },
      reading: { band: 6, score: 25, max_score: 40 },
    },
  },
]

const demoLeaderboardEntries: LeaderboardEntry[] = [
  { rank: 1, user_id: 'demo-maya', name: 'Maya R.', avatar: '', avg_band: 8, avg_percentage: 88, tests_taken: 12, section_scores: {} },
  { rank: 2, user_id: 'demo-jonas', name: 'Jonas K.', avatar: '', avg_band: 7.5, avg_percentage: 83, tests_taken: 10, section_scores: {} },
  { rank: 3, user_id: 'demo-user', name: 'Noura Al-Khatib', avatar: '', avg_band: 6.5, avg_percentage: 74, tests_taken: 7, section_scores: {} },
  { rank: 4, user_id: 'demo-lina', name: 'Lina S.', avatar: '', avg_band: 6.5, avg_percentage: 72, tests_taken: 6, section_scores: {} },
  { rank: 5, user_id: 'demo-sam', name: 'Sam T.', avatar: '', avg_band: 6, avg_percentage: 68, tests_taken: 5, section_scores: {} },
]

function demoLeaderboardFor(period: 'all' | 'monthly' | 'weekly'): LeaderboardResponse {
  const visibleCount = period === 'weekly' ? 3 : period === 'monthly' ? 4 : 5
  const leaderboard = demoLeaderboardEntries.slice(0, visibleCount)
  const currentUser = leaderboard.find((entry) => entry.user_id === demoProfile.user.id) || null
  return {
    leaderboard,
    current_user: currentUser,
    current_user_rank: currentUser?.rank ?? null,
    percentile: currentUser ? 78 : null,
    top_percentage: currentUser ? 22 : null,
    progression: [
      { date: '2026-06-01', band: 5.5, percentage: 62 },
      { date: '2026-07-01', band: 6, percentage: 68 },
      { date: '2026-08-01', band: 6.5, percentage: 74 },
    ],
    stats: {
      total_users: period === 'all' ? 248 : period === 'monthly' ? 96 : 41,
      total_tests_completed: period === 'all' ? 1_842 : period === 'monthly' ? 318 : 87,
      active_test_takers: period === 'all' ? 126 : period === 'monthly' ? 83 : 34,
    },
  }
}

export const demoLeaderboards: Record<'all' | 'monthly' | 'weekly', LeaderboardResponse> = {
  all: demoLeaderboardFor('all'),
  monthly: demoLeaderboardFor('monthly'),
  weekly: demoLeaderboardFor('weekly'),
}
